import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, MapPinIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { OpportunityForm } from '../../components/forms/OpportunityForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { StageProgressBar } from '../../components/ui/StageProgressBar'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import type { Opportunity, OpportunityInput, Account, Contact, Address } from '../../../shared/types'

const opportunitiesApi = createCrudApi<Opportunity, OpportunityInput>('opportunities')
const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const addressesApi = createCrudApi<Address, unknown>('addresses')

function formatAddress(address: Address): string {
  return `${address.street}, ${address.city}${address.state ? `, ${address.state}` : ''} ${address.postalCode || ''}, ${address.country}`
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [isEditingAddress, setIsEditingAddress] = useState(false)
  const [editShippingAddressId, setEditShippingAddressId] = useState<string | null>(null)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('opportunities')

  const { data: opportunity, isLoading } = useQuery({
    queryKey: ['opportunities', id],
    queryFn: () => opportunitiesApi.get(id!),
    enabled: !!id,
  })

  // Fetch account details for the autocomplete display
  const { data: account } = useQuery({
    queryKey: ['accounts', opportunity?.accountId],
    queryFn: () => accountsApi.get(opportunity!.accountId),
    enabled: !!opportunity?.accountId,
  })

  // Fetch contact details for the autocomplete display
  const { data: contact } = useQuery({
    queryKey: ['contacts', opportunity?.contactId],
    queryFn: () => contactsApi.get(opportunity!.contactId!),
    enabled: !!opportunity?.contactId,
  })

  // Fetch shipping address (if a specific one is selected)
  const { data: shippingAddress } = useQuery({
    queryKey: ['addresses', opportunity?.shippingAddressId],
    queryFn: () => addressesApi.get(opportunity!.shippingAddressId!),
    enabled: !!opportunity?.shippingAddressId,
  })

  // Always fetch account's addresses for the inline editor
  const { data: accountAddressesResponse } = useQuery({
    queryKey: ['addresses', 'byAccount', opportunity?.accountId],
    queryFn: () => addressesApi.list({ filters: { accountId: opportunity!.accountId }, limit: 50 }),
    enabled: !!opportunity?.accountId,
  })

  const accountAddresses = accountAddressesResponse?.data || []
  const accountPrimaryAddress = accountAddresses.find(a => a.type === 'primary')
  const shippingAddresses = accountAddresses.filter(a => a.type === 'shipping')
  const displayShippingAddress = opportunity?.shippingAddressId ? shippingAddress : accountPrimaryAddress

  const updateMutation = useMutation({
    mutationFn: (data: Partial<OpportunityInput>) => opportunitiesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', id] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => opportunitiesApi.delete(id!),
    onSuccess: () => {
      navigate('/opportunities')
    },
  })

  // Address update mutation
  const updateAddressMutation = useMutation({
    mutationFn: (shippingAddressId: string | null) =>
      opportunitiesApi.update(id!, { shippingAddressId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', id] })
      queryClient.invalidateQueries({ queryKey: ['addresses'] })
      setIsEditingAddress(false)
    },
  })

  const startEditingAddress = () => {
    setEditShippingAddressId(opportunity?.shippingAddressId ?? null)
    setIsEditingAddress(true)
  }

  const saveAddressEdit = () => {
    updateAddressMutation.mutate(editShippingAddressId)
  }

  const cancelAddressEdit = () => {
    setIsEditingAddress(false)
  }

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!opportunity) return <div>{t('pages.opportunities.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/opportunities"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.opportunities.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{opportunity.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{opportunity.stage}</p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.opportunities.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            {canRead('amount') && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.amount')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {opportunity.amount ? `$${parseFloat(opportunity.amount).toLocaleString()}` : '-'}
                </dd>
              </div>
            )}
            {canRead('stage') && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.stage')}</dt>
                <dd className="mt-1">
                  <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-brand-100 text-brand-800">
                    {opportunity.stage}
                  </span>
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.closeDate')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(opportunity.closeDate).toLocaleDateString()}
              </dd>
            </div>
            {canRead('probability') && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.probability')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{opportunity.probability || '-'}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.account')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account ? (
                  <Link to={`/accounts/${account.id}`} className="text-brand-600 hover:text-brand-900">
                    {account.name}
                  </Link>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.shipTo')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {displayShippingAddress ? (
                  <div className="flex items-start gap-1">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <span>
                      {displayShippingAddress.street}, {displayShippingAddress.city}
                      {displayShippingAddress.state && `, ${displayShippingAddress.state}`}
                      {displayShippingAddress.postalCode && ` ${displayShippingAddress.postalCode}`}, {displayShippingAddress.country}
                      {!opportunity.shippingAddressId && (
                        <span className="text-xs text-gray-500 block">(Default)</span>
                      )}
                    </span>
                  </div>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(opportunity.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="opportunities" entityId={id!} ownerId={opportunity.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="opportunities" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/opportunities"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.opportunities.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.opportunities.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{opportunity.name}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.opportunities.detailSubtitle')}</p>
            </div>
            {/* Stage Progress Bar */}
            <div className="mt-6 pb-2">
              <StageProgressBar stage={opportunity.stage} />
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <OpportunityForm
                defaultValues={nullToUndefined(opportunity)}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                accountName={account?.name}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.name')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {opportunity.name}
                  </dd>
                </div>
                {canRead('amount') && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('fields.amount')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {opportunity.amount ? `$${parseFloat(opportunity.amount).toLocaleString()}` : '-'}
                    </dd>
                  </div>
                )}
                {canRead('stage') && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('fields.stage')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-brand-100 text-brand-800">
                        {opportunity.stage}
                      </span>
                    </dd>
                  </div>
                )}
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.closeDate')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {new Date(opportunity.closeDate).toLocaleDateString()}
                  </dd>
                </div>
                {canRead('probability') && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('fields.probability')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {opportunity.probability || '-'}
                    </dd>
                  </div>
                )}
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.account')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account ? (
                      <Link to={`/accounts/${account.id}`} className="text-brand-600 hover:text-brand-900">
                        {account.name}
                      </Link>
                    ) : opportunity.accountId}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.contact')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {contact ? (
                      <Link to={`/contacts/${contact.id}`} className="text-brand-600 hover:text-brand-900">
                        {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                      </Link>
                    ) : '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {t('fields.shipTo')}
                    {!isEditing && !isEditingAddress && (
                      <button
                        onClick={startEditingAddress}
                        className="text-gray-400 hover:text-gray-600"
                        title="Edit shipping address"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {isEditingAddress ? (
                      <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('fields.selectAddress')}
                          </label>
                          <select
                            value={editShippingAddressId || ''}
                            onChange={(e) => setEditShippingAddressId(e.target.value || null)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                          >
                            {accountPrimaryAddress && (
                              <option value="">
                                Default: {formatAddress(accountPrimaryAddress)}
                              </option>
                            )}
                            {!accountPrimaryAddress && (
                              <option value="">No default address</option>
                            )}
                            {shippingAddresses.map((address) => (
                              <option key={address.id} value={address.id}>
                                {formatAddress(address)}
                              </option>
                            ))}
                          </select>
                          {shippingAddresses.length === 0 && !accountPrimaryAddress && (
                            <p className="mt-1 text-xs text-gray-500">
                              No shipping addresses found. Add addresses on the account page first.
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={saveAddressEdit}
                            disabled={updateAddressMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            {updateAddressMutation.isPending ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={cancelAddressEdit}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : displayShippingAddress ? (
                      <div className="flex items-start gap-2">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p>{displayShippingAddress.street}</p>
                          <p>
                            {displayShippingAddress.city}
                            {displayShippingAddress.state && `, ${displayShippingAddress.state}`}
                            {displayShippingAddress.postalCode && ` ${displayShippingAddress.postalCode}`}
                          </p>
                          <p>{displayShippingAddress.country}</p>
                          {!opportunity.shippingAddressId && (
                            <p className="text-xs text-gray-500 mt-1">
                              (Using account's default address)
                            </p>
                          )}
                        </div>
                      </div>
                    ) : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  )
}
